import React, { useEffect, useState, useCallback, useContext, useRef } from 'react';
import DailyIframe from '@daily-co/daily-js';
import { DailyAudio, DailyProvider } from '@daily-co/daily-react';
import posthog from 'posthog-js';
import { useSparks } from '../../../contexts/SparksContext';

import api from '../api';
import { roomUrlFromPageUrl, pageUrlFromRoomUrl } from '../utils';
import {
  persistJoinContext,
  readJoinContextForRoom,
} from '../joinContextStorage';

import UpcomingLessons from '../../UpcomingLessons';

import { loadTutorEmails } from '../tutorEmails';

import HomeScreen from '../HomeScreen/HomeScreen';
import Call from '../Call/Call';
//import Header from '../Header/Header';
import Tray from '../Tray/Tray';
import HairCheck from '../HairCheck/HairCheck';

import { AuthContext } from '../../../contexts/AuthContext';
import { useNavVisibility } from '../../../contexts/NavVisibilityContext';
import { useIsPhone } from '../../../hooks/useMediaQuery';

/* We decide what UI to show to users based on the state of the app, which is dependent on the state of the call object. */
const STATE_IDLE = 'STATE_IDLE';
const STATE_CREATING = 'STATE_CREATING';
const STATE_JOINING = 'STATE_JOINING';
const STATE_JOINED = 'STATE_JOINED';
const STATE_LEAVING = 'STATE_LEAVING';
const STATE_ERROR = 'STATE_ERROR';
const STATE_HAIRCHECK = 'STATE_HAIRCHECK';

// The server join token has a 90s TTL. Re-resolve if we're within this many ms
// of expiry so `confirm` never receives an already-dead token.
const JOIN_TOKEN_EXPIRY_SKEW_MS = 15000;
const SESSION_REFRESHED_MESSAGE =
  'Your session refreshed — please click Join call again.';

function isJoinTokenFresh(context) {
  if (!context?.joinVersionToken) return false;
  if (!context?.joinVersionExpiresAt) return false;
  const expiresMs = new Date(context.joinVersionExpiresAt).getTime();
  if (Number.isNaN(expiresMs)) return false;
  return expiresMs - Date.now() > JOIN_TOKEN_EXPIRY_SKEW_MS;
}

function isExpiredTokenError(error) {
  const status = error?.response?.status;
  const message = error?.response?.data?.message || '';
  return status === 400 && /token has expired|invalid join version token/i.test(message);
}

// Message shown when a pasted ?roomUrl=... link fails the server-side access
// check. Steers the child back to the Join Lesson button, which runs the full
// authorized resolve/confirm flow.
function messageForVerifyReason(reason) {
  switch (reason) {
    case 'no_access':
      return 'Your subscription isn’t active, so you can’t join this class yet. Please check your account, then go back and use the Join Lesson button.';
    case 'wrong_classroom':
      return 'This isn’t the class you’re assigned to. Please go back and use the Join Lesson button to join your class.';
    default:
      return 'We couldn’t verify your class access. Please go back and use the Join Lesson button to join your class.';
  }
}

export default function App() {
  const { loginChild } = useContext(AuthContext);
  const sparks = useSparks();
  const [appState, setAppState] = useState(STATE_IDLE);
  const [roomUrl, setRoomUrl] = useState(null);
  const [callObject, setCallObject] = useState(null);
  const [apiError, setApiError] = useState(false);
  const [joinContext, setJoinContext] = useState(null);
  const [joinStatusMessage, setJoinStatusMessage] = useState('');
  const [isRefreshingAssignment, setIsRefreshingAssignment] = useState(false);

  const [tutorEmails, setTutorEmails] = useState([]);
  const [layoutMode, setLayoutMode] = useState('viewport');
  const currentClassroomName = roomUrl ? roomUrl.split('/').pop() : null;

  // Refs so the Daily meeting-state handler (subscribed once, on [callObject])
  // reads the *current* join context / room without re-subscribing on every
  // change. joinCaptureRef guards class_joined/class_left to fire once per join.
  const joinContextRef = useRef(joinContext);
  const roomUrlRef = useRef(roomUrl);
  const joinCaptureRef = useRef(null);
  useEffect(() => { joinContextRef.current = joinContext; }, [joinContext]);
  useEffect(() => { roomUrlRef.current = roomUrl; }, [roomUrl]);

  // Children recognise the tutor by Daily's owner flag; this list is only the fallback,
  // so a final failure is logged rather than blocking the join.
  useEffect(() => {
    let cancelled = false;
    loadTutorEmails()
      .then((emails) => {
        if (!cancelled) setTutorEmails(emails);
      })
      .catch((error) => {
        console.error('Error fetching tutors after retries:', error);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Create a new call room. This function will return the newly created room URL.
   * We'll need this URL when pre-authorizing (https://docs.daily.co/reference/rn-daily-js/instance-methods/pre-auth)
   * or joining (https://docs.daily.co/reference/rn-daily-js/instance-methods/join) a call.
   */
  const createCall = useCallback((childId, lessonScheduleId) => {
    setAppState(STATE_CREATING);
    setJoinStatusMessage('');
    setIsRefreshingAssignment(false);

    if (!lessonScheduleId) {
      setApiError('Unable to join lesson: missing lesson schedule reference.');
      setAppState(STATE_IDLE);
      return Promise.resolve(null);
    }

    return api
      .getRoomChild(childId, lessonScheduleId)
      .then((response) => {
        loginChild(response.child);  // Login with the returned child object
        const nextContext = {
          childId,
          lessonScheduleId,
          joinVersionToken: response.joinDecision?.joinVersionToken || null,
          joinVersionExpiresAt: response.joinDecision?.joinVersionExpiresAt || null,
          idempotencyKey: response.joinIdempotencyKey,
        };
        setJoinContext(nextContext);
        persistJoinContext(response.url, nextContext);
        return response.url;  // Return the URL for the video call
      })
      .catch((error) => {
        console.error('Error creating room', error);
        setRoomUrl(null);
        setJoinContext(null);
        setAppState(STATE_IDLE);
        setApiError(
          error?.response?.data?.message ||
            error?.message ||
            'Could not prepare lesson join. Please try again.',
        );
      });
  }, [loginChild]);

  /**
   * We've created a room, so let's start the hair check. We won't be joining the call yet.
   */
  const startHairCheck = useCallback(async (url) => {
    const newCallObject = DailyIframe.createCallObject({
      subscribeToTracksAutomatically: false  // Set this to false by default
    });


    setRoomUrl(url);
    setCallObject(newCallObject);
    setAppState(STATE_HAIRCHECK);
    await newCallObject.preAuth({ url }); // add a meeting token here if your room is private
    await newCallObject.startCamera();
  }, []);

  /**
   * Once we pass the hair check, we can actually join the call.
   * We'll pass the username entered during Haircheck to .join().
   */
  const joinCall = useCallback(async (userName, displayName) => {
    if (!callObject) return;

    // No lesson context (e.g. page opened directly via ?roomUrl=... with no
    // matching sessionStorage entry) means we cannot run resolve/confirm.
    // Join the Daily room directly so the user is not stranded.
    if (!joinContext?.childId || !joinContext?.lessonScheduleId) {
      callObject.join({
        url: roomUrl,
        userName,
        userData: {
          displayName: displayName,
          ...(joinContext?.childId != null && { mongoChildId: String(joinContext.childId) }),
        }
      });
      return;
    }

    let refreshedContext = { ...joinContext };

    // Re-resolve for a fresh token when the current one is missing, or expired /
    // about to expire (server TTL is 90s). This covers a restored sessionStorage
    // context and, crucially, the case where the child lingered on the hair-check
    // screen long enough for the token to age out before clicking Join. Without
    // this the confirm call 400s with "token has expired".
    const refreshTokenContext = async (idempotencyKey) => {
      const refreshed = await api.getRoomChild(
        refreshedContext.childId,
        refreshedContext.lessonScheduleId,
      );
      refreshedContext = {
        ...refreshedContext,
        joinVersionToken: refreshed.joinDecision?.joinVersionToken || refreshedContext.joinVersionToken,
        joinVersionExpiresAt:
          refreshed.joinDecision?.joinVersionExpiresAt || refreshedContext.joinVersionExpiresAt || null,
        idempotencyKey: idempotencyKey || refreshed.joinIdempotencyKey || refreshedContext.idempotencyKey,
      };
      setJoinContext(refreshedContext);
      if (refreshed?.url) {
        setRoomUrl(refreshed.url);
        persistJoinContext(refreshed.url, refreshedContext);
      } else {
        persistJoinContext(roomUrl, refreshedContext);
      }
    };

    if (!isJoinTokenFresh(refreshedContext)) {
      await refreshTokenContext();
    }

    for (let attempt = 0; attempt < 2; attempt += 1) {
      let confirmResult;
      try {
        confirmResult = await api.confirmJoinRoom({
          childId: refreshedContext.childId,
          lessonScheduleId: refreshedContext.lessonScheduleId,
          joinVersionToken: refreshedContext.joinVersionToken,
          idempotencyKey: `${refreshedContext.idempotencyKey}:confirm:${attempt}`,
        });
      } catch (error) {
        // A token can still expire in the brief window between our freshness
        // check and the request landing. Re-resolve once and retry rather than
        // surfacing a raw 400 to the user.
        if (isExpiredTokenError(error) && attempt === 0) {
          setIsRefreshingAssignment(true);
          setJoinStatusMessage('Refreshing your session…');
          await refreshTokenContext();
          setIsRefreshingAssignment(false);
          setJoinStatusMessage('');
          continue;
        }
        throw error;
      }

      if (confirmResult.status !== 'stale_token') {
        // Trial Star (+1,500) lands on the second distinct class join, in the join reply (§2.3).
        sparks.pushFromResponse(confirmResult);
        if (!confirmResult.allowedRoom) {
          const requestId = confirmResult?.requestId ? ` (request ${confirmResult.requestId})` : '';
          throw new Error(`No classroom assignment available${requestId}`);
        }

        setJoinStatusMessage('');
        setIsRefreshingAssignment(false);
        const assignedRoomUrl = `https://novalearning1.daily.co/${confirmResult.allowedRoom}`;
        setRoomUrl(assignedRoomUrl);
        persistJoinContext(assignedRoomUrl, refreshedContext);
        callObject.join({
          url: assignedRoomUrl,
          userName,
          userData: {
            displayName: displayName,
            ...(refreshedContext.childId != null && { mongoChildId: String(refreshedContext.childId) }),
          }
        });
        return;
      }

      setIsRefreshingAssignment(true);
      setJoinStatusMessage('Refreshing assignment…');
      await new Promise((resolve) => setTimeout(resolve, 3500));
      await refreshTokenContext(`${refreshedContext.idempotencyKey}:refresh:${attempt}`);
    }

    setIsRefreshingAssignment(false);
    setJoinStatusMessage('');
    throw new Error(SESSION_REFRESHED_MESSAGE);
  }, [callObject, joinContext, roomUrl]);

  /**
   * Start leaving the current call.
   */
  const startLeavingCall = useCallback(() => {
    if (!callObject) return;
    // If we're in the error state, we've already "left", so just clean up
    if (appState === STATE_ERROR) {
      callObject.destroy().then(() => {
        setRoomUrl(null);
        setCallObject(null);
        setAppState(STATE_IDLE);
      });
    } else {
      /* This will trigger a `left-meeting` event, which in turn will trigger
      the full clean-up as seen in handleNewMeetingState() below. */
      setAppState(STATE_LEAVING);
      callObject.leave();
    }
  }, [callObject, appState]);

  /**
   * If a room's already specified in the page's URL when the component mounts,
   * join the room.
   */
  useEffect(() => {
    const url = roomUrlFromPageUrl();
    if (!url) return;

    const restored = readJoinContextForRoom(url);
    if (restored) {
      // Returning to a call we already authorized (e.g. a page refresh mid-call):
      // trust the stored context and proceed straight to the hair check.
      setJoinContext(restored);
      startHairCheck(url);
      return;
    }

    // Raw ?roomUrl=... paste with no local join context. Before letting the
    // child into the room, verify with the server that (a) the account has
    // access and (b) one of its children is actually assigned to this class.
    // Fail closed: if we can't confirm, send them to the Join Lesson button.
    let cancelled = false;
    (async () => {
      try {
        const result = await api.verifyRoomAccess(url);
        if (cancelled) return;
        if (result?.allowed) {
          startHairCheck(url);
        } else {
          setApiError(messageForVerifyReason(result?.reason));
          setAppState(STATE_IDLE);
        }
      } catch (error) {
        if (cancelled) return;
        setApiError(messageForVerifyReason('verify_failed'));
        setAppState(STATE_IDLE);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startHairCheck]);

  /**
   * Update the page's URL to reflect the active call when roomUrl changes.
   */
  useEffect(() => {
    const pageUrl = pageUrlFromRoomUrl(roomUrl);
    if (pageUrl === window.location.href) return;
    window.history.replaceState(null, null, pageUrl);
  }, [roomUrl]);

  /**
   * Update app state based on reported meeting state changes.
   *
   * NOTE: Here we're showing how to completely clean up a call with destroy().
   * This isn't strictly necessary between join()s, but is good practice when
   * you know you'll be done with the call object for a while, and you're no
   * longer listening to its events.
   */
  useEffect(() => {
    if (!callObject) return;

    const events = ['joined-meeting', 'left-meeting', 'error', 'camera-error'];

    function handleNewMeetingState() {
      switch (callObject.meetingState()) {
        case 'joined-meeting':
          setAppState(STATE_JOINED);
          // Forensic marker: anchor the replay timeline to the moment the child
          // actually entered the classroom. Guarded so re-fires of this handler
          // (reconnects, re-renders) don't emit duplicate joins.
          if (!joinCaptureRef.current) {
            const ctx = joinContextRef.current || {};
            const classroom = roomUrlRef.current ? roomUrlRef.current.split('/').pop() : null;
            joinCaptureRef.current = { joinedAtMs: Date.now() };
            posthog.capture('class_joined', {
              childId: ctx.childId != null ? String(ctx.childId) : null,
              lessonScheduleId: ctx.lessonScheduleId != null ? String(ctx.lessonScheduleId) : null,
              classroom,
              roomUrl: roomUrlRef.current || null,
            });
          }
          break;
        case 'left-meeting':
          // Forensic marker: bounds the session and records dwell time. A
          // class_joined with NO matching class_left = dropped without a clean
          // leave (tab close / crash), which is itself a useful bug signal.
          if (joinCaptureRef.current) {
            const ctx = joinContextRef.current || {};
            const classroom = roomUrlRef.current ? roomUrlRef.current.split('/').pop() : null;
            const durationSeconds = Math.round(
              (Date.now() - joinCaptureRef.current.joinedAtMs) / 1000,
            );
            posthog.capture('class_left', {
              childId: ctx.childId != null ? String(ctx.childId) : null,
              lessonScheduleId: ctx.lessonScheduleId != null ? String(ctx.lessonScheduleId) : null,
              classroom,
              durationSeconds,
            });
            joinCaptureRef.current = null;
          }
          callObject.destroy().then(() => {
            setRoomUrl(null);
            setCallObject(null);
            setAppState(STATE_IDLE);
          });
          break;
        case 'error':
          setAppState(STATE_ERROR);
          break;
        default:
          break;
      }
    }

    // Use initial state
    handleNewMeetingState();

    /*
     * Listen for changes in state.
     * We can't use the useDailyEvent hook (https://docs.daily.co/reference/daily-react/use-daily-event) for this
     * because right now, we're not inside a <DailyProvider/> (https://docs.daily.co/reference/daily-react/daily-provider)
     * context yet. We can't access the call object via daily-react just yet, but we will later in Call.js and HairCheck.js!
     */
    events.forEach((event) => callObject.on(event, handleNewMeetingState));

    // Stop listening for changes in state
    return () => {
      events.forEach((event) => callObject.off(event, handleNewMeetingState));
    };
  }, [callObject]);

    // Add cleanup on unmount
  useEffect(() => {
    // Cleanup function that runs when component unmounts
    return () => {
      if (callObject) {
        callObject.destroy().then(() => {
          setRoomUrl(null);
          setCallObject(null);
          setAppState(STATE_IDLE);
        });
      }
    };
  }, [callObject]);

  // Add navigation event listener
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (callObject) {
        callObject.leave();
        callObject.destroy();
      }
    };

    // Listen for navigation events
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // For React Router navigation
    window.addEventListener('popstate', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handleBeforeUnload);
    };
  }, [callObject]);

  /**
   * Show the call UI if we're either joining, already joined, or have encountered
   * an error that is _not_ a room API error.
   */
  const showCall = !apiError && [STATE_JOINING, STATE_JOINED, STATE_ERROR].includes(appState);

  // On phones the purple nav would sit above a full-height call; hide it for the
  // whole call so the lesson, tutor tile and tray get the entire screen.
  const { setCallNavHidden } = useNavVisibility();
  const isPhone = useIsPhone();
  useEffect(() => {
    if (!isPhone || !showCall) return undefined;
    setCallNavHidden(true);
    return () => setCallNavHidden(false);
  }, [isPhone, showCall, setCallNavHidden]);

  /* When there's no problems creating the room and startHairCheck() has been successfully called,
   * we can show the hair check UI. */
  const showHairCheck = !apiError && appState === STATE_HAIRCHECK;

  const renderApp = () => {
    // If something goes wrong with creating the room.
    if (apiError) {
      return (
        <div className="api-error">
          <h1>Error</h1>
          <p>{typeof apiError === 'string' ? apiError : 'Room could not be created.'}</p>
        </div>
      );
    }

    if (showHairCheck || showCall) {
      return (
        <DailyProvider callObject={callObject}>
          {showHairCheck ? (
            // No API errors? Let's check our hair then.
            <HairCheck
              joinCall={joinCall}
              cancelCall={startLeavingCall}
              joinStatusMessage={joinStatusMessage}
              isRefreshingAssignment={isRefreshingAssignment}
            />
          ) : layoutMode === 'main-like' ? (
            // Tutor, lesson on, not focused: main-like layout – no 100vh, page scrolls, Tray below content
            <div className="call-and-tray call-and-tray--main-like" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Call
                tutorEmails={tutorEmails}
                onLayoutModeChange={setLayoutMode}
                classroomName={currentClassroomName}
                lessonScheduleId={joinContext?.lessonScheduleId ?? null}
              />
              <Tray tutorEmails={tutorEmails} leaveCall={startLeavingCall} />
              <DailyAudio />
            </div>
          ) : (
            // Viewport layout: 100vh so Tray always visible (student or tutor in focus)
            <div className="call-and-tray max-md:!h-[100dvh]" style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <Call
                  tutorEmails={tutorEmails}
                  onLayoutModeChange={setLayoutMode}
                  classroomName={currentClassroomName}
                  lessonScheduleId={joinContext?.lessonScheduleId ?? null}
                />
              </div>
              <Tray tutorEmails={tutorEmails} leaveCall={startLeavingCall} />
              <DailyAudio />
            </div>
          )}
        </DailyProvider>
      );
    }

    // The default view is the HomeScreen, from where we start the demo.
    return <UpcomingLessons createCall={createCall} startHairCheck={startHairCheck} />;
  };

  return (
    <div className="app">
      {/* <Header /> */}
      {renderApp()}
    </div>
  );
}